import React, { useState, useEffect } from 'react';
import { Shield, Lock, Monitor, Info, Check, ChevronDown, ChevronUp, Eye, EyeOff, CircleAlert } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser, fetchUserAction } from '../stores/userSlice';
import ApiClient from '../ApiClient';
import { notify } from '../utils/ToastUtils';
import TwoFactorSetup from '../components/TwoFactorSetup';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { AccountSettingsSkeleton } from '../components/PageSkeletonLoaders';
import moment from 'moment';

const AccountSettings = () => {
    const dispatch = useDispatch();
    const user = useSelector(selectUser);
    const [activeTab, setActiveTab] = useState('security');
    const [expandedSection, setExpandedSection] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    
    const [is2FAEnabled, setIs2FAEnabled] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

    useEffect(() => {
        const fetch2FAStatus = async () => {
            try {
                const res = await ApiClient.get(
                    `${process.env.REACT_APP_BASE_URL}/2fa/status`
                );

                setIs2FAEnabled(res.data.enabled);
            } catch (error) {
                console.error("Failed to fetch 2FA status:", error);
            } finally {
                setIsLoading(false);
            }
        };

        dispatch(fetchUserAction());
        fetch2FAStatus();
    }, [dispatch]);

    const getPasswordSubtitle = () => {
        if (user?.password_last_updated) {
            return `Last changed on ${moment(user.password_last_updated).format("DD-MMM-YYYY")}`;
        }
        if (user?.account_created_date) {
            return `Set on account creation (${moment(user.account_created_date).format("DD-MMM-YYYY")})`;
        }
        return "Never changed";
    };

    const toggleSection = (section) => {
        setExpandedSection(prev => prev === section ? null : section);
    };

    if (isLoading) {
        return <AccountSettingsSkeleton />;
    }

    return (
        <div className="flex flex-col w-full px-4 lg:px-6 pb-6 gap-6">

            <div className="flex flex-col lg:flex-row gap-6 items-start">

                <div className="flex-1 w-full bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                    {activeTab === 'security' && (
                        <div className="flex flex-col gap-4">
                            <div className="mb-2">
                                <h2 className="text-lg font-bold text-slate-800">Security Settings</h2>
                                <p className="text-slate-500 text-sm">Keep your account secure with these settings</p>
                            </div>

                            {/* Two-Factor Authentication */}
                            <TwoFactorSetup 
                                isExpanded={expandedSection === '2fa'} 
                                onToggle={() => toggleSection('2fa')} 
                                is2FAEnabled={is2FAEnabled}
                                onStatusChange={setIs2FAEnabled}
                            />

                            {/* Password Section */}
                            <div className="flex flex-col border border-slate-200 rounded-xl overflow-hidden transition-all bg-white">
                                <div 
                                    className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-4 px-4 sm:px-5 cursor-pointer hover:bg-slate-50/80 transition-colors"
                                    // onClick={() => setIsPasswordModalOpen(true)}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="p-2 rounded-lg bg-blue-50 text-blue-600 shrink-0">
                                            <Lock size={20} />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-slate-800 text-sm">Password</h3>
                                            <p className="text-sm text-slate-500 mt-0.5">{getPasswordSubtitle()}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0 sm:self-start md:self-center">
                                        <button 
                                            className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg font-semibold text-sm hover:bg-slate-50 transition-colors shrink-0 bg-white shadow-sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsPasswordModalOpen(true);
                                            }}
                                        >
                                            Change Password
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Active Sessions Section */}
                            {/* <div className={`flex flex-col border border-slate-200 rounded-xl overflow-hidden transition-all ${expandedSection === 'sessions' ? 'border-t-4 border-t-blue-500 bg-slate-50/50 shadow-sm' : 'bg-white'}`}>
                                <div 
                                    className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-4 px-4 sm:px-5 cursor-pointer hover:bg-slate-50/80 transition-colors"
                                    onClick={() => toggleSection('sessions')}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="p-2 rounded-lg bg-blue-50 text-blue-600 shrink-0">
                                            <Monitor size={20} />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-slate-800 text-sm">Active Sessions</h3>
                                            <p className="text-sm text-slate-500 mt-0.5">You are currently logged in.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0 sm:self-start md:self-center">
                                        <button disabled className="px-4 py-2 border border-slate-200 text-slate-400 rounded-lg font-semibold text-sm cursor-not-allowed opacity-50 shrink-0 bg-white">
                                            Manage Sessions
                                        </button>
                                    </div>
                                </div>
                                
                                {expandedSection === 'sessions' && (
                                    <div className="px-4 sm:px-5 pb-5 pt-2 border-t border-slate-100 bg-white">
                                        <p className="text-sm text-slate-600">Active session details will appear here.</p>
                                    </div>
                                )}
                            </div> */}

                            {/* Security Tips */}
                            <div className="mt-2 bg-blue-50/50 border border-blue-100 rounded-xl p-4 flex items-start gap-3">
                                <Info className="text-blue-500 shrink-0 mt-0.5" size={20} />
                                <div>
                                    <h4 className="font-semibold text-blue-800 text-sm mb-2">Security Tips</h4>
                                    <ul className="space-y-2">
                                        <li className="flex items-center gap-2 text-sm text-slate-600">
                                            <Check size={16} className="text-emerald-500 shrink-0" />
                                            Use a strong password with a mix of letters, numbers and symbols.
                                        </li>
                                        <li className="flex items-center gap-2 text-sm text-slate-600">
                                            <Check size={16} className="text-emerald-500 shrink-0" />
                                            Enable 2FA to keep your account secure.
                                        </li>
                                        <li className="flex items-center gap-2 text-sm text-slate-600">
                                            <Check size={16} className="text-emerald-500 shrink-0" />
                                            Never share your login credentials with anyone.
                                        </li>
                                    </ul>
                                </div>
                            </div>

                        </div>
                    )}
                </div>
            </div>

            <ChangePasswordModal 
                isOpen={isPasswordModalOpen} 
                toggle={() => setIsPasswordModalOpen(false)} 
                is2FAEnabled={is2FAEnabled}
                onSuccess={() => dispatch(fetchUserAction())}
            />
        </div>
    );
};

export default AccountSettings;
